import React, { useMemo } from 'react';
import { Grid, Stack } from '@mui/material';
import { BlogHOC } from '@components/BlogSection/BlogComponents/HOC/BlogHOC';
import { blogCategoryStyles as classes } from './style/blogCategory';
import { useAtom } from 'jotai';
import { navHeightAtom, selectedLanguageAtom } from '@atoms/appSettings';
import { getTranslation } from '@helpers/languageTranslations';
import { useTranslation } from '@stateManagement/TranslationContext';
import { ensureInternalURLTrailingSlash } from '@helpers/string';
import { languageRouteTypes } from '@helpers/languageRoutes';
import { getLanguageRouteAndPrefixByCodeAndRouteKey } from '@helpers/languages';
import { BlogCard1 } from '@components/BlogCards/BlogCard1';
import { useStaticValues } from '@stateManagement/StaticValuesContext';
import { Heading, Text, Pagination, PaginationItem, LazyLoadContainer } from '@web-for-marketing/react-ui';
import { LanguageCode } from '@models/languageCode';
import { BlogCategoryNav } from '@components/BlogSection/BlogComponents/BlogPage/BlogCategoryNav';
import { FeaturedCategory } from '@components/BlogSection/BlogComponents/Shared/FeaturedCategory';
import { BlogContainer } from '@components/BlogSection/BlogComponents/Home/BlogContainer';
import { ArticlesWithSubscribe } from '@components/BlogSection/BlogComponents/Shared/ArticlesWithSubscribe';

export default function BlogCategory(): React.ReactElement {
    const [navHeight] = useAtom(navHeightAtom);
    const [selectedLanguage] = useAtom(selectedLanguageAtom);
    const { selectedLanguageTranslation } = useTranslation();
    const { blog, prefixStringWithBaseUrl } = useStaticValues();
    const blogHomeTranslation = useMemo(
        () => getTranslation(selectedLanguageTranslation, 'blogHome'),
        [selectedLanguageTranslation]
    );
    const noBlogsFoundForCategoryTranslation = useMemo(
        () => getTranslation(selectedLanguageTranslation, 'noBlogsFound'),
        [selectedLanguageTranslation]
    );
    const categoryPageData = blog.categoryPageData;
    const categoryBlogPosts = categoryPageData?.pagedBlogArticles;
    const showFeaturedBlogs = (categoryPageData?.featuredBlogs?.length ?? 0) >= 3;
    const showNewestPosts = (categoryPageData?.recentBlogs?.length ?? 0) >= 3;
    const categoryBlogLink = useMemo(
        () => generateBlogLink(selectedLanguage, languageRouteTypes.CATEGORY),
        [selectedLanguage]
    );
    const languageBlogLink = useMemo(
        () => generateBlogLink(selectedLanguage, languageRouteTypes.BLOG_HOME),
        [selectedLanguage]
    );

    const categoryNavLinks =
        blog.allCategories?.map((categoryData) => ({
            text: categoryData.name,
            link: `${categoryBlogLink}${categoryData.slug}`,
        })) || [];

    const shouldShowLatestArticlesHeading =
        !showFeaturedBlogs && !showNewestPosts && (categoryBlogPosts?.pageRecords?.length ?? 0) > 0;

    function generateBlogLink(selectedLanguage: LanguageCode, routeKey: 'categoryUrl' | 'blogHomeUrl'): string {
        return getLanguageRouteAndPrefixByCodeAndRouteKey(selectedLanguage, routeKey);
    }

    function getPageUrlPrefix(categorySlug: string): string {
        return ensureInternalURLTrailingSlash(
            `${getLanguageRouteAndPrefixByCodeAndRouteKey(
                selectedLanguage,
                languageRouteTypes.CATEGORY
            )}${categorySlug}`
        );
    }

    return (
        <BlogHOC type='categoryPage' noIndex={categoryPageData?.category.noIndex}>
            <BlogCategoryNav categoryLinks={categoryNavLinks} />
            {categoryPageData ? (
                <div css={{ width: '100%' }}>
                    <BlogContainer>
                        <Heading variant='h1' css={showFeaturedBlogs ? classes.mainHeader : { marginBottom: '2rem' }}>
                            {categoryPageData.category.name}
                        </Heading>
                        {shouldShowLatestArticlesHeading ? (
                            <Heading variant='h2' weight='bold' color='activePrimary'>
                                {blogHomeTranslation.viewAll}
                            </Heading>
                        ) : null}
                        {showFeaturedBlogs ? (
                            <React.Fragment>
                                <Text
                                    variant='h4'
                                    component='h2'
                                    weight='bold'
                                    color='activePrimary'
                                    css={classes.sectionTitle}
                                >
                                    {blogHomeTranslation.featured}
                                </Text>
                                <FeaturedCategory
                                    articles={categoryPageData.featuredBlogs}
                                    categoryBlogLink={categoryBlogLink}
                                    languageBlogLink={languageBlogLink}
                                />
                            </React.Fragment>
                        ) : null}
                    </BlogContainer>
                    {showNewestPosts ? (
                        <div css={classes.newestPosts}>
                            <BlogContainer>
                                <Heading variant='h2' weight='bold' color='activePrimary' css={classes.sectionTitle}>
                                    {blogHomeTranslation.newestPosts}
                                </Heading>
                                <ArticlesWithSubscribe
                                    articles={categoryPageData.recentBlogs}
                                    categoryBlogLink={categoryBlogLink}
                                    languageBlogLink={languageBlogLink}
                                    lazyLoad
                                />
                            </BlogContainer>
                        </div>
                    ) : null}
                    {categoryBlogPosts?.pageRecords?.length !== 0 ? (
                        <BlogContainer>
                            <LazyLoadContainer
                                component={Grid}
                                container
                                spacing={{ xs: 2, md: 4 }}
                                id='categoryArticles'
                                css={{ scrollMarginTop: `${navHeight}px` }}
                            >
                                {categoryBlogPosts?.pageRecords.map((blogPost, index) => {
                                    return (
                                        <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={blogPost.slug}>
                                            <BlogCard1
                                                data-testid={`blogPostCard${index}`}
                                                title={blogPost.title}
                                                linkPath={`${languageBlogLink}${blogPost.slug}`}
                                                image={{
                                                    imagePath: prefixStringWithBaseUrl(blogPost.cardImageUrl),
                                                    imageAlt: blogPost.cardImageAlt,
                                                }}
                                                categoryPillData={{
                                                    category: blogPost.category.name,
                                                    categoryLink: `${categoryBlogLink}${blogPost.category.slug}`,
                                                }}
                                                ariaLabel={`Read more about ${blogPost.title}`}
                                                body={blogPost.description}
                                                publishedDateUtc={blogPost.publishedDateUtc ?? ''}
                                                readMinutes={blogPost.readMinutes}
                                                lazyLoad
                                            />
                                        </Grid>
                                    );
                                })}
                            </LazyLoadContainer>
                            {categoryPageData.pagedBlogArticles.totalRecordCount >
                            categoryPageData.pagedBlogArticles.maxRecordsPerPage ? (
                                    <Stack alignItems='center' css={{ padding: '2rem' }}>
                                        <Pagination
                                            currentPage={categoryPageData.pagedBlogArticles.currentPage}
                                            totalRecordCount={categoryPageData.pagedBlogArticles.totalRecordCount}
                                            maxRecordsPerPage={categoryPageData.pagedBlogArticles.maxRecordsPerPage}
                                            renderItem={(values) => (
                                            // eslint-disable-next-line jsx-a11y/anchor-has-content
                                                (<PaginationItem
                                                    component='a'
                                                    href={`${getPageUrlPrefix(categoryPageData.category.slug ?? '')}${values.page}/#categoryArticles`}
                                                    {...values}
                                                />)
                                            )}
                                        />
                                    </Stack>
                                ) : null}
                        </BlogContainer>
                    ) : (
                        <BlogContainer>
                            <Text variant='h2' color='tertiary' data-testid='no-blogs-message'>
                                {noBlogsFoundForCategoryTranslation.forCategory} {categoryPageData?.category.name}.
                            </Text>
                        </BlogContainer>
                    )}
                </div>
            ) : null}
        </BlogHOC>
    );
}
